import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
  Renderer2,
} from '@angular/core';
import { COLUMN_MIN_WIDTH } from '@qtek/shared/models';

@Directive({
  selector: 'mat-header-cell[qtResizable]',
  standalone: false,
})
export class ColumnResizeDirective implements AfterViewInit, OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly ngZone = inject(NgZone);

  qtResizable = input<boolean>(true);
  qtResizableMin = input<number>(COLUMN_MIN_WIDTH);
  qtResizableMax = input<number | undefined>(undefined);
  /** Column id — used to query all cells of this column for live DOM resize. */
  qtResizableColumnId = input<string>('');

  columnResize = output<number>();

  private startX = 0;
  private startWidth = 0;
  private maxWidth = Infinity;
  private dragging = false;
  private tableEl: Element | null = null;
  private unlistenHandleMouseDown: (() => void) | null = null;
  private unlistenMouseMove: (() => void) | null = null;
  private unlistenMouseUp: (() => void) | null = null;
  private dragCursorStyleEl: HTMLStyleElement | null = null;
  private pendingClientX: number | null = null;
  private rafId: number | null = null;

  ngAfterViewInit(): void {
    if (!this.qtResizable()) return;
    this.attachHandle();
  }

  ngOnDestroy(): void {
    this.unlistenHandleMouseDown?.();
    this.removeDocumentListeners();
    this.cancelPendingRaf();
    this.stopDragCursor();
  }

  private attachHandle(): void {
    const handle: HTMLElement = this.renderer.createElement('div');
    this.renderer.addClass(handle, 'qt-data-table__resize-handle');
    this.renderer.appendChild(this.el.nativeElement, handle);
    this.unlistenHandleMouseDown = this.renderer.listen(
      handle,
      'mousedown',
      (e: MouseEvent) => this.onMouseDown(e)
    );
  }

  private onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.startX = event.clientX;
    this.startWidth = this.el.nativeElement.offsetWidth;
    this.dragging = true;
    this.tableEl = this.el.nativeElement.closest('mat-table');
    this.maxWidth = this.computeMaxWidthFromStickyGeometry();
    this.startDragCursor();

    // Run document listeners outside Angular zone — mousemove fires at pointer
    // frequency (200+ Hz) and must not trigger change detection on every event.
    // Native addEventListener used instead of Renderer2.listen because Renderer2
    // wraps callbacks in zone.run() regardless of the current zone context.
    this.ngZone.runOutsideAngular(() => {
      const onMove = (e: MouseEvent) => this.onMouseMove(e);
      const onUp = (e: MouseEvent) => this.onMouseUp(e);
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      this.unlistenMouseMove = () =>
        document.removeEventListener('mousemove', onMove);
      this.unlistenMouseUp = () =>
        document.removeEventListener('mouseup', onUp);
    });
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.dragging) return;
    // Always capture the latest position; only schedule one RAF per frame.
    this.pendingClientX = event.clientX;
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      if (this.pendingClientX !== null) {
        this.applyWidthToDom(this.computeWidth(this.pendingClientX));
        this.pendingClientX = null;
      }
    });
  }

  private onMouseUp(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.dragging) return;
    this.dragging = false;
    this.cancelPendingRaf();
    this.stopDragCursor();
    const width = this.computeWidth(event.clientX);
    this.applyWidthToDom(width);
    this.removeDocumentListeners();
    this.suppressNextClick();
    // Re-enter zone only for the emit — this is what triggers Angular CD and
    // updates the signal + sticky column recalculation in the component.
    this.ngZone.run(() => {
      this.columnResize.emit(width);
    });
    this.tableEl = null;
  }

  /**
   * Injects a temporary <style> that locks the cursor to col-resize across the
   * entire document. Setting cursor on body alone is not enough — child elements
   * with their own cursor rule (Material buttons, text nodes, etc.) override it
   * via CSS specificity. !important on * beats every element-level rule.
   */
  private startDragCursor(): void {
    this.dragCursorStyleEl = document.createElement('style');
    this.dragCursorStyleEl.textContent =
      '*, *::before, *::after { cursor: col-resize !important; user-select: none !important; }';
    document.head.appendChild(this.dragCursorStyleEl);
  }

  private stopDragCursor(): void {
    this.dragCursorStyleEl?.remove();
    this.dragCursorStyleEl = null;
  }

  /**
   * The browser fires a click event after every mousedown+mouseup pair, even
   * when the mouseup belongs to a drag. Angular Material sort listens on click,
   * so without this the header sort toggles on every resize-end.
   * A one-shot capture listener intercepts it before any other handler sees it.
   */
  private suppressNextClick(): void {
    const remove = () => document.removeEventListener('click', handler, true);
    const handler = (e: Event) => {
      e.stopImmediatePropagation();
      e.preventDefault();
      remove();
    };
    document.addEventListener('click', handler, true);
    // Guard: if the mouse was released outside the window no click will follow,
    // so remove the listener after a short window to avoid swallowing an
    // unrelated click when the user returns to the page.
    setTimeout(remove, 250);
  }

  /** Directly mutates cell styles during drag — no Angular CD involved. */
  private applyWidthToDom(width: number): void {
    const colId = this.qtResizableColumnId();
    if (!colId || !this.tableEl) return;
    this.tableEl
      .querySelectorAll(`[data-column-id="${CSS.escape(colId)}"]`)
      .forEach(cell => {
        const el = cell as HTMLElement;
        el.style.minWidth = `${width}px`;
        el.style.maxWidth = `${width}px`;
        el.style.flexBasis = `${width}px`;
      });
  }

  /**
   * Constrains drag so the column never grows into sticky-column territory.
   * Angular CDK marks sticky cells with .mat-mdc-table-sticky (not inline
   * position styles). All sticky columns — left and right — are included so
   * the resizable column can never completely obscure the non-sticky zone.
   * The column being resized is excluded from the sum via data-column-id.
   */
  private computeMaxWidthFromStickyGeometry(): number {
    if (!this.tableEl) return Infinity;
    const scrollContainer = this.tableEl.parentElement;
    if (!scrollContainer) return Infinity;

    const currentColId = this.qtResizableColumnId();
    const stickyCells = Array.from(
      this.tableEl.querySelectorAll<HTMLElement>('mat-header-cell')
    ).filter(
      cell =>
        cell.classList.contains('mat-mdc-table-sticky') &&
        cell.getAttribute('data-column-id') !== currentColId
    );

    if (!stickyCells.length) return Infinity;

    const containerWidth = scrollContainer.clientWidth;
    // Seed with 100 to keep a minimum comfort gap beyond the sticky columns.
    const stickyWidth = stickyCells.reduce(
      (sum, cell) => sum + cell.offsetWidth,
      100
    );

    return Math.max(this.qtResizableMin(), containerWidth - stickyWidth);
  }

  private computeWidth(clientX: number): number {
    const raw = this.startWidth + (clientX - this.startX);
    const min = this.qtResizableMin();
    const columnMax = this.qtResizableMax();
    const effectiveMax =
      columnMax != null ? Math.min(columnMax, this.maxWidth) : this.maxWidth;
    return Math.min(Math.max(raw, min), effectiveMax);
  }

  private cancelPendingRaf(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.pendingClientX = null;
  }

  private removeDocumentListeners(): void {
    this.unlistenMouseMove?.();
    this.unlistenMouseUp?.();
    this.unlistenMouseMove = null;
    this.unlistenMouseUp = null;
  }
}
